import { useEffect, useRef, useState } from 'react';
import passkeysUtils from 'utils/passkeys';
import { loadBeyondIdentityUtils } from 'utils/lazyPasskey';
import { classifyPasskeyError, logPasskeyForDynatrace } from 'utils/passkeyDynatrace';

const DETECTION_STATUS = {
    LOADING: 'loading',
    NONE: 'none',
    SINGLE: 'single',
    MULTIPLE: 'multiple'
};

const noop = () => {};

/**
 * @param {{ skip?: boolean }} [options] - when `skip` is true the SDK preflight is
 *   short-circuited and status resolves to NONE. Used by SIM-24 AC-11 so a /signin
 *   visit landing with `passkeyOffered=1` (trigger already prompted and the user
 *   declined) does not re-load Beyond Identity or re-prompt the user.
 */
const usePasskeyDetection = ({ skip = false } = {}) => {
    const [status, setStatus] = useState(DETECTION_STATUS.LOADING);
    const [passkeys, setPasskeys] = useState([]);
    const hasRun = useRef(false);

    useEffect(() => {
        if (hasRun.current) {
            return noop;
        }

        hasRun.current = true;

        if (skip || !passkeysUtils.isPasskeyEnabled()) {
            setStatus(DETECTION_STATUS.NONE);

            return noop;
        }

        let cancelled = false;

        (async () => {
            try {
                const isSupported = await passkeysUtils.isPasskeySupported();

                if (!isSupported || cancelled) {
                    if (!cancelled) {
                        setStatus(DETECTION_STATUS.NONE);
                    }

                    return;
                }

                const BeyondIdentityUtils = await loadBeyondIdentityUtils();
                await BeyondIdentityUtils.initialize();
                const devicePasskeys = await BeyondIdentityUtils.getPasskeys();

                if (cancelled) {
                    return;
                }

                if (!devicePasskeys?.length) {
                    setStatus(DETECTION_STATUS.NONE);
                } else if (devicePasskeys.length === 1) {
                    setPasskeys(devicePasskeys);
                    setStatus(DETECTION_STATUS.SINGLE);
                } else {
                    setPasskeys(devicePasskeys);
                    setStatus(DETECTION_STATUS.MULTIPLE);
                }
            } catch (error) {
                if (!cancelled) {
                    setStatus(DETECTION_STATUS.NONE);
                    logPasskeyForDynatrace(classifyPasskeyError(error), 'Detection', error, 'signin-page');
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    return { status, passkeys, DETECTION_STATUS };
};

export default usePasskeyDetection;

export { DETECTION_STATUS };
